import { differenceInHours } from 'date-fns';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import type { RecentSearch } from 'src/common/types/common/RecentSearch';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import { FULL_DAY_HOURS } from 'src/listing/constants';
import { CaptainOptionValue } from '../types/CaptainOptions';

export type RecentSearchCookie = Pick<RecentSearch, 'capacity' | 'captained' | 'dates' | 'max_price' | 'min_price' | 'duration' | 'captainOptionValue' | 'departureTime'>;
export type RecentInquiryData = Pick<RecentSearch, 'capacity' | 'dates' | 'max_price' | 'min_price' | 'departureTime'> & {
  captained: boolean;
  captainOptionValue: CaptainOptionValue;
  bareboat: boolean;
  duration: string;
};

const PAGE_SIZE = 5;

/**
 * Whenever cookie version is updated, ensure that it should be updated in
 * react-universal https://github.com/getmyboat/react-universal/blob/master/src/common/utils/recentInquiryCookieData.ts
 */
export const RECENT_INQUIRY_VERSION = 'v4';
const COOKIE_FIELD_COUNT = 10;

/**
  Set RECENT_SEARCH_COOKIE in following pattern in specific order:
  - version;
  - capacity;
  - dates;
  - captained;
  - captainOptionValue;
  - min_price;
  - max_price;

  The cookie is comma-separated. Values are position base so any values
  not included will be kept as empty like a sparse array i.e. an
  empty cookie would look like: `,,,,,,`

  e.g.
    - version: v1
    - capacity: 3
    - dates: ['2024-11-10','2024-11-11','2024-11-12']
    - captained: true
    - captainOptionValue: 1 | 2 | 4
    - duration: PT2H (2 hours)
    - min_price: 10
    - max_price: 999
    - departureTime: `05:00`
    - => v4,3,2024-11-10,2,1,PT2H,10,999,05:00

  e.g.
    - version: v1
    - capacity: 3
    - dates: ['2024-11-10']
    - captained: false
    - captainOptionValue: 1 | 2 | 4
    - duration: ''
    - min_price: 10
    - max_price: null
    - departureTime: null
    - => v4,3,2024-11-10,,,10,
*/
export const constructRecentSearchCookie = ({
  capacity,
  dates,
  captained,
  captainOptionValue,
  duration,
  min_price: minPrice,
  max_price: maxPrice,
  departureTime,
}: RecentSearchCookie): string => {
  const datesSearch = () => {
    const datesCount = dates.length - 1;
    return datesCount > 0 ? datesCount : '';
  };
  const captainedSearch = () => {
    if (typeof captained !== 'string') return '';
    return captained === 'true' ? '1' : '0';
  };

  const durationSearch = () => (duration || '');
  const captainOptionValueSearch = () => (captainOptionValue ? `${captainOptionValue}` : '');

  return `${RECENT_INQUIRY_VERSION},${capacity ?? ''},${dates[0] ?? ''},${datesSearch()},${captainedSearch()},${captainOptionValueSearch()},${durationSearch()},${minPrice ?? ''},${maxPrice ?? ''},${departureTime ?? ''}`;
};

const isPositiveInt = (value: string): boolean => {
  if (!value) return false;
  const intValue = parseInt(value, 10);
  return typeof intValue === 'number' && intValue >= 0;
};

// Only exported for testing validation
export const getValidRecentSearchCookieValues = (cookie: string): {
  version: string;
  cookieCapacity: string;
  cookieDate: string;
  cookieDateCount: string;
  cookieCaptained: string;
  cookieCaptainedOptionValue: string;
  cookieDuration: string;
  cookieMinPrice: string;
  cookieMaxPrice: string;
  cookieDepartureTime: string;
} | null => {
  const cookieValues = cookie.split(',');

  const [
    version,
    cookieCapacity,
    cookieDate,
    cookieDateCount,
    cookieCaptained,
    cookieCaptainedOptionValue,
    cookieDuration,
    cookieMinPrice,
    cookieMaxPrice,
    cookieDepartureTime,
  ] = cookieValues;
  if (version !== RECENT_INQUIRY_VERSION || cookieValues.length !== COOKIE_FIELD_COUNT) {
    return null;
  }
  return {
    version,
    cookieCapacity,
    cookieDate,
    cookieDateCount,
    cookieCaptained,
    cookieCaptainedOptionValue,
    cookieDuration,
    cookieMinPrice,
    cookieMaxPrice,
    cookieDepartureTime,
  };
};
/**
  * Translate saved recent search cookie to js object in following pattern:

  v3,3,2024-11-10,2,4,1,PT2H,10,999
  = {
    capacity: 3,
    dates: ['2024-11-10', '2024-11-11', '2024-11-12],
    captained: true,
    captainOptionValue: 4,
    duration: PT2H,
    min_price: '10',
    max_price: '999',
  }

  v1,3,2024-11-10,,0,10,
  = {
    capacity: 3,
    dates: ['2024-11-10'],
    captained: false,
    min_price: '10',
  }
*/
export const parseRecentSearchCookieToObject = (
  cookie: string,
): Partial<RecentInquiryData> => {
  const cookieValues = getValidRecentSearchCookieValues(cookie);

  if (!cookieValues) {
    return {
      dates: [],
    };
  }
  const {
    cookieCapacity,
    cookieDate,
    cookieDateCount,
    cookieCaptained,
    cookieCaptainedOptionValue,
    cookieDuration,
    cookieMinPrice,
    cookieMaxPrice,
    cookieDepartureTime,
  } = cookieValues;

  const data: Partial<RecentInquiryData> = {};
  const capacityValue = isPositiveInt(cookieCapacity) ? parseInt(cookieCapacity, 10) : null;
  if (capacityValue) {
    data.capacity = capacityValue.toString();
  }

  // Primary date and +- dates parsing
  const today = todayDate();
  let mainDate: Date | null = null;
  const extraDays = parseInt(cookieDateCount, 10);

  data.dates = [];
  if (cookieDate) {
    mainDate = newTZDate(cookieDate);
    let isValid = mainDate >= today;

    let halfExtraDays = extraDays
      ? Math.ceil(extraDays / 2)
      : 0;

    // main date is in the past, find a valid date in the range
    if (!isValid) {
      const dayDiff = Math.ceil(differenceInHours(today, mainDate) / FULL_DAY_HOURS);
      if (dayDiff <= halfExtraDays) {
        mainDate = today;
        isValid = true;
        // since mainDate is moved forwards, move the halfDays back the same amount
        halfExtraDays -= dayDiff;
      }
    }

    if (isValid) {
      data.dates = [formatDate(mainDate)];
      for (let dateOffset = 1; dateOffset <= halfExtraDays; dateOffset += 1) {
        const beforeDate = subDays(mainDate, dateOffset);
        if (beforeDate >= today) {
          data.dates.push(formatDate(beforeDate));
        }
        const afterDate = addDays(mainDate, dateOffset);
        if (afterDate >= today) {
          data.dates.push(formatDate(afterDate));
        }
      }
    }
  }

  if (isPositiveInt(cookieCaptained) && (['0', '1'].includes(cookieCaptained))) {
    data.captained = cookieCaptained === '1';
    data.bareboat = cookieCaptained === '0';
  }

  if (cookieCaptainedOptionValue) {
    data.captainOptionValue = cookieCaptainedOptionValue as unknown as CaptainOptionValue;
  }

  if (cookieDuration) {
    data.duration = cookieDuration;
  }

  const minPrice = isPositiveInt(cookieMinPrice) ? parseInt(cookieMinPrice, 10) : null;
  const maxPrice = isPositiveInt(cookieMaxPrice) ? parseInt(cookieMaxPrice, 10) : null;

  if (minPrice !== null) data.min_price = minPrice.toString();
  if (maxPrice !== null) data.max_price = maxPrice.toString();

  // Ensure min < max when both exist
  if (minPrice && maxPrice
    && maxPrice < minPrice) {
    delete data.min_price;
    delete data.max_price;
  }
  if (cookieDepartureTime) {
    // TODO: validate?
    data.departureTime = cookieDepartureTime;
  }

  return data;
};

/**
  * Translate saved recent search cookie to search params in following pattern:

  3,2024-11-10,2,1,10,999
  = &capacity=3&dates=2024-11-10&dates=2024-11-11&dates=2024-11-12
    &captained=true&min_price=10&max_price=999

  3,2024-11-10,,0,10,
  = &capacity=3&dates=2024-11-10&captained=false&min_price=10
  NOTE: departure time will be ignored/discarded
*/
export const parseRecentSearchCookieToSearchParams = (
  cookie: string,
): URLSearchParams => {
  const searchParams = new URLSearchParams({ page_size: `${PAGE_SIZE}` });
  const cookieObj = parseRecentSearchCookieToObject(cookie);
  Object.keys(cookieObj).forEach((key) => {
    // Discard departure time
    if (key === 'departureTime') {
      return;
    }
    if (key === 'dates') {
      const dates = cookieObj[key];
      dates?.forEach((date) => searchParams.append(key, date));
    } else if (key === 'captained' || key === 'bareboat' || cookieObj[key]) {
      searchParams.append(key, cookieObj[key]);
    }
  });

  return searchParams;
};
